// src/library/toggle.tsx
import { Button, ColorMode, Flex, View, ThemeProvider, defaultDarkModeOverride } from "@aws-amplify/ui-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { enableConfigureAgents } from "../endpoints";

const darkTheme = {
  name: "my-theme",
  overrides: [defaultDarkModeOverride],
};

type PageWrapperProps = {
  children: React.ReactNode;
  hideToggle?: boolean;
};

const THEME_STORAGE_KEY = "ui-color-mode";

function getInitialColorMode(): ColorMode {
  // Try saved preference
  try {
    const saved = (typeof window !== "undefined" && localStorage.getItem(THEME_STORAGE_KEY)) as ColorMode | null;
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    /* ignore */
  }
  // Fall back to system preference
  try {
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)")?.matches) {
      return "dark";
    }
  } catch {
    /* ignore */
  }
  return "light";
}

function PageWrapper(props: PageWrapperProps) {
  const nav = useNavigate();
  const location = useLocation();

  const [theme, setTheme] = useState<ColorMode>(getInitialColorMode());
  const isLight = theme === "light";

  // Persist theme + react to system changes if user hasn't explicitly set one yet
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  // If the user never explicitly toggled, listen to system changes (best effort)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");

    const saved = (() => {
      try {
        return localStorage.getItem(THEME_STORAGE_KEY);
      } catch {
        return null;
      }
    })();

    if (saved === "light" || saved === "dark") return; // user has explicit choice

    const handleChange = () => {
      setTheme(mql.matches ? "dark" : "light");
    };
    try {
      mql.addEventListener?.("change", handleChange);
    } catch {
      mql.addListener?.(handleChange);
    }
    return () => {
      try {
        mql.removeEventListener?.("change", handleChange);
      } catch {
        mql.removeListener?.(handleChange);
      }
    };
  }, []);

  const isChat = useMemo(() => location.pathname.startsWith("/chat"), [location.pathname]);

  const onTogglePage = useCallback(() => {
    nav(isChat ? "/configuration" : "/chat");
  }, [isChat, nav]);

  const onToggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeProvider theme={darkTheme} colorMode={theme}>
      <View
        className="page"
        backgroundColor={isLight ? "rgb(247, 246, 246)" : "black"}
        // ensure inner columns (sidebar/body) can scroll independently
        style={{ height: "100dvh", minHeight: 0, overflow: "hidden" }}
      >
        {!props.hideToggle && (
          <Flex className="themeTogglePanel">
            {enableConfigureAgents && (
              <Button
                className="iconButton"
                onClick={onTogglePage}
                aria-label={isChat ? "Go to configuration" : "Go to chat"}
                title={isChat ? "Configuration" : "Chat"}
              >
                {!isChat && <i className="fa-solid fa-comment" aria-hidden="true" />}
                {isChat && <i className="fa-solid fa-wrench" aria-hidden="true" />}
              </Button>
            )}
            <Button
              className="iconButton"
              onClick={onToggleTheme}
              aria-label={isLight ? "Switch to dark theme" : "Switch to light theme"}
              title={isLight ? "Dark mode" : "Light mode"}
            >
              {!isLight && <i className="fa-solid fa-moon" aria-hidden="true" />}
              {isLight && <i className="fa-solid fa-sun" aria-hidden="true" />}
            </Button>
          </Flex>
        )}
        {props.children}
      </View>
    </ThemeProvider>
  );
}

export default PageWrapper;
